/**
 * Date and time formatting utilities — SSOT for every date/time on screen.
 *
 * Port of trading-bot-app's lib/utils/dates.ts: Intl-based, driven by the
 * user's Display preferences (timezone, date format, time format) from
 * settingsStore. Helpers read the store at call time, so plain call sites
 * honor the prefs on their next render; components that must re-render the
 * moment a preference changes use hooks/useTimezone instead.
 *
 * All timestamps are stored ISO-8601 UTC and converted for display only.
 * Contract EXPIRY strings are symbol identity, not timestamps — they do
 * NOT flow through these (bot-identical).
 */
import { formatDistanceToNow, parseISO, isValid, format as dfFormat } from 'date-fns'
import {
  useSettingsStore, type DateFormat as DateFormatType,
  type TimeFormat as TimeFormatType,
} from '@/stores/settingsStore'

export interface DateTimeOptions {
  timezone?: string
  dateFormat?: DateFormatType
  timeFormat?: TimeFormatType
}

function prefs(): Required<DateTimeOptions> {
  const s = useSettingsStore.getState()
  return { timezone: s.timezone, dateFormat: s.dateFormat, timeFormat: s.timeFormat }
}

/**
 * Parse into a Date. Date-ONLY strings (YYYY-MM-DD) are taken as local
 * calendar dates (no UTC midnight shift); full timestamps parse normally.
 * Bot parity: parseLocalDate.
 */
function parseLocalDate(value: string | Date): Date {
  if (value instanceof Date) return value
  const s = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-').map(Number)
    return new Date(y, m - 1, d)
  }
  const d = parseISO(s)
  return isValid(d) ? d : new Date(s)
}

/** Date in the user's preferred format + timezone. Falsy/invalid → "-". */
export function formatDate(
  value: string | Date | undefined | null,
  options?: DateTimeOptions,
): string {
  if (!value) return '-'
  const d = parseLocalDate(value)
  if (isNaN(d.getTime())) return '-'
  const { timezone, dateFormat } = { ...prefs(), ...options }

  if (dateFormat === 'iso') {
    const parts = new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit', timeZone: timezone,
    }).formatToParts(d)
    const get = (t: string) => parts.find((p) => p.type === t)?.value
    return `${get('year')}-${get('month')}-${get('day')}`
  }
  if (dateFormat === 'us') {
    return d.toLocaleDateString('en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit', timeZone: timezone,
    })
  }
  if (dateFormat === 'eu') {
    return d.toLocaleDateString('en-GB', {
      year: 'numeric', month: '2-digit', day: '2-digit', timeZone: timezone,
    })
  }
  return d.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', timeZone: timezone,
  })
}

/** Date + time in the user's preferred formats + timezone. */
export function formatDateTime(
  value: string | Date | undefined | null,
  options?: DateTimeOptions,
): string {
  if (!value) return '-'
  const d = parseLocalDate(value)
  if (isNaN(d.getTime())) return '-'
  const { timezone, dateFormat, timeFormat } = { ...prefs(), ...options }
  const hour12 = timeFormat === '12h'

  if (dateFormat === 'iso') {
    const parts = new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12, timeZone: timezone,
    }).formatToParts(d)
    const get = (t: string) => parts.find((p) => p.type === t)?.value
    const period = get('dayPeriod')
    const time = hour12
      ? `${get('hour')}:${get('minute')}:${get('second')} ${period ?? ''}`.trim()
      : `${get('hour')}:${get('minute')}:${get('second')}`
    return `${get('year')}-${get('month')}-${get('day')} ${time}`
  }
  const locale = dateFormat === 'eu' ? 'en-GB' : 'en-US'
  const month: Intl.DateTimeFormatOptions['month'] =
    dateFormat === 'long' ? 'short' : '2-digit'
  const day: Intl.DateTimeFormatOptions['day'] =
    dateFormat === 'long' ? 'numeric' : '2-digit'
  return d.toLocaleString(locale, {
    year: 'numeric', month, day,
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12, timeZone: timezone,
  })
}

/** Wall-clock time in the user's preferred format + timezone. */
export function formatTime(
  value: string | Date | undefined | null,
  options?: DateTimeOptions,
): string {
  if (!value) return '-'
  const d = parseLocalDate(value)
  if (isNaN(d.getTime())) return '-'
  const { timezone, timeFormat } = { ...prefs(), ...options }
  return d.toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: timeFormat === '12h', timeZone: timezone,
  })
}

/** ISO → "5 minutes ago" / "2 hours ago" / "yesterday". */
export function formatRelative(value: string | Date | undefined | null): string {
  if (!value) return '-'
  const d = typeof value === 'string' ? parseISO(value) : value
  if (!isValid(d)) return '-'
  return formatDistanceToNow(d, { addSuffix: true })
}

/** ISO → YYYY-MM-DD (canonical API/date-param format — NOT pref-driven). */
export function formatISODate(value: string | Date | undefined | null): string {
  if (!value) return '-'
  const d = typeof value === 'string' ? parseISO(value) : value
  if (!isValid(d)) return '-'
  return dfFormat(d, 'yyyy-MM-dd')
}
